import { Coins, Plus } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { ProgressBar } from "../ui/progressBar";
import type { CreditsDetails } from "../../types/membership";
import { useAuth } from "../../context/AuthContext";
import { dictionary } from "../../utils/dictionary";
import { ButtonFluxi } from "../common/Buttons/ButtonFluxi/ButtonFluxi";
import { Alert } from "../common/information/Alert";
import { convertToZone } from "../../utils/formatters";
import { format } from "date-fns";
import { useState } from "react";
import { ModalWrapper } from "../modals/ModalWrapper";
import { BuyCreditsModal } from "../modals/BuyCreditsModal";

interface CreditsSectionProps {
  credits: CreditsDetails;
  nextRenewalDate: string;
}

export function CreditsSection({ credits, nextRenewalDate }: CreditsSectionProps) {
  const { language } = useAuth();
  const formatDate = "dd/MM/yyyy";

  const [isOpenBuyCreditsModal, setIsOpenBuyCreditsModal] = useState(false);

  const closeModal = () => {
    setIsOpenBuyCreditsModal(false);
  };
  
  const nextRenewalDateFormatted = convertToZone(
    format(new Date(nextRenewalDate || ""), formatDate),
    formatDate
  );

  const formatNumber = (num: number) => {
    return num.toLocaleString("es-ES");
  };

  const handleBuyCredits = () => {
    setIsOpenBuyCreditsModal(true);
  };

  return (
    <>
      {isOpenBuyCreditsModal && (
        <ModalWrapper
          isOpen={isOpenBuyCreditsModal}
          onClose={closeModal}
        >
         <BuyCreditsModal
          onClose={closeModal}
          nextRenewalDate={nextRenewalDate}
          language={language}
        />
        </ModalWrapper>
      )}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center mb-6 justify-between align-middle">
            <div className="flex items-center gap-2">
              <Coins className="w-5 h-5 text-gray-600" />
              <h2 className="text-xs font-semibold text-gray-900">
                {dictionary["credits"][language]}
              </h2>
            </div>
            <ButtonFluxi icon={<Plus className="w-5 h-5 text-white" />} text={dictionary["buy-credits"][language]} onClick={handleBuyCredits} style={{ fontSize: "12px", paddingTop: "10px", paddingBottom: "10px", paddingLeft: "18px", paddingRight: "18px", maxHeight: "40px" }} />
          </div>

          <div className="text-center mb-6">
            <div className="text-4xl font-bold text-gray-900 mb-2">
              {formatNumber(credits.credits_available)}
            </div>
            <p className="text-gray-600">
              de {formatNumber(credits.total_credits)}{" "}
              {dictionary["available"][language]}
            </p>
          </div>
          
          <div className="mb-6">
            <Alert 
              title={dictionary["remember-use-credits"][language]} 
              description={
                <>
                  {dictionary["you-must-spend-these-credits-before"][language]} <strong className="font-bold">{nextRenewalDateFormatted}</strong> {dictionary["when-your-credits-will-be-renewed"][language]}
                </>
              } 
              type="warning" 
            />
          </div>

          <div className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-600">
                  {dictionary["credits-usage"][language]}
                </span>
                <span className="text-sm font-medium text-gray-900">
                  {credits.percentage_used}%
                </span>
              </div>
              <ProgressBar value={credits.percentage_used} max={100} />
            </div>

            <div className="grid grid-cols-1 gap-3 pt-4 border-t border-gray-200">
              <div className="flex justify-between">
                <span className="text-gray-600">
                  {dictionary["credits-used"][language]}
                </span>
                <span className="font-medium text-gray-900">
                  {formatNumber(credits.credits_used)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">
                  {dictionary["credits-remaining"][language]}
                </span>
                <span className="font-medium text-gray-900">
                  {formatNumber(credits.credits_available)}
                </span>
              </div>
              <div className="flex justify-between border-t border-gray-200 pt-3">
                <span className="font-medium text-gray-900">
                  {dictionary["plan-total"][language]}
                </span>
                <span className="font-bold text-gray-900">
                  {formatNumber(credits.total_credits)}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
